import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  createCogsAdjustmentRequest,
  getCogsAdjustmentRequestByOrderItem,
  getCogsAdjustmentRequests,
  updateCogsAdjustmentRequest,
} from '~/services/OrdersService';

import type {
  CogsAdjustmentRequestResponse,
  GetCogsAdjustmentRequestsQuery,
  UpdateCogsAdjustmentRequestBody,
} from '~/types/services/cogsAdjustmentRequest';

export const cogsAdjustmentRequestQueryKeys = {
  all: ['cogs-adjustment-request'] as const,
  lists: () => [...cogsAdjustmentRequestQueryKeys.all, 'list'] as const,
  list: (query: GetCogsAdjustmentRequestsQuery) => [...cogsAdjustmentRequestQueryKeys.lists(), query] as const,
  byOrderItem: (orderId: string, itemId: string) =>
    [...cogsAdjustmentRequestQueryKeys.all, 'order', orderId, 'item', itemId] as const,
};

export function useGetCogsAdjustmentRequests(query: GetCogsAdjustmentRequestsQuery) {
  return useQuery({
    queryKey: cogsAdjustmentRequestQueryKeys.list(query),
    queryFn: ({ signal }) => getCogsAdjustmentRequests(query, signal),
    // Keep the previous page visible while a new page/filter is fetched so DataGrid does not reset pagination.
    placeholderData: keepPreviousData,
  });
}

export function useGetCogsAdjustmentRequestByOrderItem(orderId: string, itemId: string, enabled = true) {
  return useQuery({
    queryKey: cogsAdjustmentRequestQueryKeys.byOrderItem(orderId, itemId),
    queryFn: () => getCogsAdjustmentRequestByOrderItem(orderId, itemId),
    enabled: enabled && !!orderId && !!itemId,
    retry: false,
  });
}

export function useCreateCogsAdjustmentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCogsAdjustmentRequest,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: cogsAdjustmentRequestQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: cogsAdjustmentRequestQueryKeys.byOrderItem(variables.orderId, variables.itemId),
      });
    },
  });
}

export function useActionCogsAdjustmentRequest() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<CogsAdjustmentRequestResponse, Error, { id: string; payload: UpdateCogsAdjustmentRequestBody }>({
    mutationFn: ({ id, payload }) => updateCogsAdjustmentRequest(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: cogsAdjustmentRequestQueryKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: cogsAdjustmentRequestQueryKeys.byOrderItem(data.result.orderId, data.result.itemId),
      });
      enqueueSnackbar('COGS adjustment request updated', { variant: 'success' });
    },
    onError: (error) => {
      if ((error as Error & { status?: number }).status === 409) {
        queryClient.invalidateQueries({ queryKey: cogsAdjustmentRequestQueryKeys.lists() });
      }
      enqueueSnackbar(`Failed to update COGS adjustment request: ${error.message}`, { variant: 'error' });
    },
  });
}
